import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PageConfig from '@/component/layout/PageConfig';
import { useQueryGetMentalHealthReports } from '@/api/mentalHealth/getMentalHealthReports';
import BCGUploadModal from '@/component/mentalHealth/BCGUploadModal';
import SolidButton from '@/component/button/SolidButton';
import BorderButton from '@/component/button/BorderButton';
import SearchBar from '@/component/input/SearchBar';
import Pagination from '@/component/pagination/Pagination';

const MentalHealthReports: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const uploadModalRef = useRef<any>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [filterStatus, setFilterStatus] = useState<string>('all');
  
  // URL에서 userId 파라미터 가져오기
  const userIdFromUrl = searchParams.get('userId');
  const filterUserId = userIdFromUrl ? Number(userIdFromUrl) : null;

  const { data: reportsData, refetch, isLoading } = useQueryGetMentalHealthReports({
    page: currentPage,
    limit: 12,
  });

  const handleUploadSuccess = () => {
    refetch();
    setCurrentPage(1);
  };

  const handleViewDetail = (reportId: number | string) => {
    navigate(`/mental-health/reports/${reportId}`);
  };

  const getStatusColor = (label: string) => {
    const healthy = t('mentalHealth.status.healthy');
    const normal = t('mentalHealth.status.normal');
    const caution = t('mentalHealth.status.caution');
    const risk = t('mentalHealth.status.risk');
    
    switch (label) {
      case healthy:
      case '건강':
        return { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-300', icon: '✅', gradient: 'from-emerald-400 to-teal-400' };
      case normal:
      case '보통':
        return { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-300', icon: '🔵', gradient: 'from-blue-400 to-cyan-400' };
      case caution:
      case '주의':
        return { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-300', icon: '⚠️', gradient: 'from-amber-400 to-orange-400' };
      case risk:
      case '위험':
        return { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-300', icon: '🚨', gradient: 'from-red-400 to-pink-400' };
      default:
        return { bg: 'bg-gray-50', text: 'text-gray-700', border: 'border-gray-300', icon: '⚪', gradient: 'from-gray-400 to-slate-400' };
    }
  };

  const getStressEmoji = (stress: string) => {
    switch (stress) {
      case '낮음': return '😌';
      case '보통': return '😐';
      case '높음': return '😰';
      case '매우 높음': return '😱';
      default: return '🤔';
    }
  };

  const filteredReports = reportsData?.reports.filter(report => {
    const matchesSearch = report.userName.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = filterStatus === 'all' || report.decisionLabel === filterStatus;
    const matchesUserId = filterUserId === null || report.userId === filterUserId;
    return matchesSearch && matchesStatus && matchesUserId;
  }) || [];

  // userId 필터링이 있을 경우, 첫 번째 리포트로 자동 이동
  useEffect(() => {
    if (filterUserId && filteredReports.length > 0) {
      // 최신 리포트로 자동 이동
      navigate(`/mental-health/reports/${filteredReports[0].id}`, { replace: true });
    }
  }, [filterUserId, filteredReports, navigate]);

  return (
    <PageConfig showTopNav={true} showFooter={true}>
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50">
        {/* 헤더 */}
        <div className="bg-gradient-to-r from-teal-600 via-emerald-600 to-green-600 text-white">
          <div className="max-w-7xl mx-auto px-6 py-10">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center space-x-4 mb-3">
                  <div className="text-5xl">📚</div>
                  <div>
                    <h1 className="text-4xl font-black">{t('mentalHealth.reports.title')}</h1>
                    <p className="text-emerald-100 text-lg mt-1">{t('mentalHealth.reports.desc')}</p>
                  </div>
                </div>
              </div>
              <div className="flex space-x-3">
                <BorderButton
                  text={t('mentalHealth.reports.button.dashboard')}
                  onClick={() => navigate('/mental-health')}
                  className="border-white text-white hover:bg-white/10"
                />
                <SolidButton
                  text={t('mentalHealth.reports.button.newAnalysis')}
                  onClick={() => uploadModalRef.current?.open()}
                  className="bg-white text-emerald-600 hover:bg-emerald-50 font-bold px-6"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-6 py-8">
          {/* 검색 및 필터 */}
          <div className="bg-white rounded-3xl p-6 shadow-lg border border-gray-100 mb-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">{t('mentalHealth.reports.search.user')}</label>
                <SearchBar
                  value={searchQuery}
                  onChanged={(value) => setSearchQuery(value)}
                  placeholder={t('mentalHealth.reports.search.user')}
                  className="w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">{t('mentalHealth.reports.filter.status')}</label>
                <div className="flex space-x-2">
                  {['all', t('mentalHealth.status.healthy'), t('mentalHealth.status.normal'), t('mentalHealth.status.caution'), t('mentalHealth.status.risk')].map((status) => {
                    const colors = status !== 'all' ? getStatusColor(status) : null;
                    return (
                      <button
                        key={status}
                        onClick={() => setFilterStatus(status)}
                        className={`px-4 py-2 rounded-xl font-semibold text-sm transition-all ${
                          filterStatus === status
                            ? colors
                              ? `${colors.bg} ${colors.text} border-2 ${colors.border}`
                              : 'bg-gray-900 text-white'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        {status === 'all' ? t('mentalHealth.reports.filter.all') : `${colors?.icon} ${status}`}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          {/* 리포트 카드 그리드 */}
          {isLoading ? (
            <div className="flex items-center justify-center py-20">
              <div className="text-center">
                <div className="text-6xl mb-4 animate-pulse">⏳</div>
                <p className="text-gray-600 font-medium">{t('common.loading')}...</p>
              </div>
            </div>
          ) : filteredReports.length > 0 ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                {filteredReports.map((report) => {
                  const statusColors = getStatusColor(report.decisionLabel);
                  return (
                    <div
                      key={report.id}
                      className="bg-white rounded-3xl shadow-lg border-2 border-gray-100 overflow-hidden hover:shadow-2xl hover:scale-105 transition-all duration-300 cursor-pointer"
                      onClick={() => handleViewDetail(report.id)}
                    >
                      {/* 카드 헤더 */}
                      <div className={`bg-gradient-to-r ${statusColors.gradient} p-6 text-white relative overflow-hidden`}>
                        <div className="absolute top-0 right-0 text-9xl opacity-10 -mr-6 -mt-6">{statusColors.icon}</div>
                        <div className="relative z-10">
                          <div className="flex items-center justify-between mb-4">
                            <div className="bg-white/20 backdrop-blur-sm px-3 py-1 rounded-full text-xs font-bold">
                              ID: {report.id}
                            </div>
                            <div className="text-3xl font-black">
                              {Math.round(report.mindScore)}
                            </div>
                          </div>
                          <div className="flex items-center space-x-3">
                            <div className="w-12 h-12 rounded-full bg-white/30 backdrop-blur-sm flex items-center justify-center text-2xl font-bold">
                              {report.userName.charAt(0)}
                            </div>
                            <div>
                              <div className="font-bold text-lg">{report.userName}</div>
                              <div className="text-xs opacity-90">{t('mentalHealth.reports.user')} #{report.userId}</div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* 카드 본문 */}
                      <div className="p-6">
                        {/* 분석 일시 */}
                        <div className="flex items-center space-x-2 text-sm text-gray-600 mb-4">
                          <span className="text-lg">📅</span>
                          <span>
                            {new Date(report.analysisDate).toLocaleDateString(i18n.language === 'ja' ? 'ja-JP' : i18n.language === 'en' ? 'en-US' : 'ko-KR', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </span>
                        </div>

                        {/* 상태 및 스트레스 */}
                        <div className="grid grid-cols-2 gap-3 mb-4">
                          <div>
                            <div className="text-xs text-gray-500 mb-1">{t('mentalHealth.modal.result.status')}</div>
                            <div className={`inline-flex items-center px-3 py-1 rounded-lg text-sm font-bold border-2 ${statusColors.bg} ${statusColors.text} ${statusColors.border}`}>
                              <span className="mr-1">{statusColors.icon}</span>
                              {report.decisionLabel}
                            </div>
                          </div>
                          <div>
                            <div className="text-xs text-gray-500 mb-1">{t('mentalHealth.stress.title')}</div>
                            <div className="inline-flex items-center px-3 py-1 rounded-lg text-sm font-medium bg-gray-100 text-gray-700">
                              <span className="mr-1">{getStressEmoji(report.stressCategory)}</span>
                              {report.stressCategory}
                            </div>
                          </div>
                        </div>

                        {/* 품질 지표 */}
                        <div className="bg-gray-50 rounded-xl p-3 mb-4">
                          <div className="text-xs text-gray-500 mb-2">{t('mentalHealth.reports.dataQuality')}</div>
                          <div className="flex items-center">
                            <div className="flex-1 bg-gray-200 rounded-full h-2 mr-3">
                              <div
                                className="bg-emerald-500 h-2 rounded-full"
                                style={{ width: `${report.quality * 100}%` }}
                              ></div>
                            </div>
                            <span className="text-sm font-bold text-gray-700">
                              {Math.round(report.quality * 100)}%
                            </span>
                          </div>
                        </div>

                        {/* 액션 버튼 */}
                        <button
                          className="w-full bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-bold py-3 rounded-xl hover:from-emerald-600 hover:to-teal-600 transition-all shadow-md hover:shadow-lg"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewDetail(report.id);
                          }}
                        >
                          {t('mentalHealth.reports.button.viewDetail')}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* 페이지네이션 */}
              {reportsData && reportsData.total > reportsData.limit && (
                <div className="flex justify-center">
                  <Pagination
                    current_page={currentPage}
                    total_page={Math.ceil(reportsData.total / reportsData.limit)}
                    fetchPage={setCurrentPage}
                  />
                </div>
              )}
            </>
          ) : (
            <div className="bg-white rounded-3xl shadow-lg p-16 text-center border-2 border-dashed border-gray-200">
              <div className="text-8xl mb-6">📊</div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">
                {searchQuery || filterStatus !== 'all' ? t('mentalHealth.empty.search') : t('mentalHealth.reports.empty')}
              </h3>
              <p className="text-gray-600 mb-6">
                {searchQuery || filterStatus !== 'all'
                  ? t('mentalHealth.empty.search')
                  : t('mentalHealth.reports.empty.upload')}
              </p>
              {!searchQuery && filterStatus === 'all' && (
                <SolidButton
                  text={t('mentalHealth.reports.button.newAnalysis')}
                  onClick={() => uploadModalRef.current?.open()}
                  className="bg-emerald-600 hover:bg-emerald-700"
                />
              )}
            </div>
          )}
        </div>

        {/* 업로드 모달 */}
        <BCGUploadModal
          ref={uploadModalRef}
          onSuccess={handleUploadSuccess}
        />
      </div>
    </PageConfig>
  );
};

export default MentalHealthReports;
